import { FC, useState } from 'react';

import {
  GiftItemInfoData,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

const cell = 'block px-5 py-2 xl:table-cell xl:py-5 xl:align-top';

export const GiftItemInfoRow: FC<{
  item: GiftItemInfoData;
  topMargin: boolean;
  brandNames: Map<string, string>;
  categoryNames: Map<string, string>;
}> = ({ item, topMargin, brandNames, categoryNames }) => {
  const [expanded, setExpanded] = useState(false);
  const brandId =
    typeof item.giftItemBrand === 'object' ? item.giftItemBrand?._id : item.giftItemBrand;
  const brandName =
    typeof item.giftItemBrand === 'object'
      ? item.giftItemBrand?.name
      : brandId
      ? brandNames.get(brandId)
      : undefined;
  const categoryId =
    typeof item.giftDeliveryCategory === 'object'
      ? item.giftDeliveryCategory?._id
      : item.giftDeliveryCategory;
  const categoryName =
    typeof item.giftDeliveryCategory === 'object'
      ? item.giftDeliveryCategory?.name
      : categoryId
      ? categoryNames.get(categoryId)
      : undefined;
  const imageUrl =
    item.image && typeof item.image === 'object'
      ? fileBucketUrl(item.image.url, 'raw')
      : '';
  const editUrl = `/gift-seller/item-info/${item._id}/edit`;
  const margin =
    item.priceRetail && item.priceWhole
      ? ((item.priceRetail - item.priceWhole) / item.priceRetail) * 100
      : null;

  return (
    <>
      <tr className="block border-t border-gray-200 py-3 xl:table-row xl:py-0">
        <td className={`${cell} xl:w-[30%]`}>
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-50 text-sm font-semibold text-gray-400">
              {imageUrl ? (
                <img src={imageUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                item.name.slice(0, 1)
              )}
            </div>
            <div className="min-w-0">
              <Link
                to={editUrl}
                className="break-words font-semibold text-gray-900 hover:underline"
              >
                {item.name}
              </Link>
              <p className="mt-1 text-xs text-gray-500">
                {item.productCode != null ? `코드 ${item.productCode}` : '코드 미설정'} ·{' '}
                {item.order != null ? `표시 순서 ${item.order}` : '순서 미설정'}
              </p>
              {item.isSelectable && (
                <p className="mt-1 text-xs text-gray-500">
                  선택형 · 선택유닛 {item.choiceUnits?.length ?? 0}개
                </p>
              )}
            </div>
          </div>
        </td>
        <td className={cell}>
          <p className="mb-1 text-xs text-gray-500 xl:hidden">브랜드 · 배송 카테고리</p>
          {brandId ? (
            <Link
              to={`/gift-seller/item-brand/${brandId}/edit`}
              aria-label={`${brandName ?? '연결'} 브랜드 수정`}
              className="break-words text-sm text-gray-700 hover:underline"
            >
              {brandName ?? '브랜드 확인'} ↗
            </Link>
          ) : (
            <span className="text-sm text-gray-400">브랜드 미지정</span>
          )}
          {item.type === GiftItemInfoType.Delivery && (
            <div className="mt-1 text-xs text-gray-500">
              {categoryId ? (
                <Link
                  to={`/gift-seller/delivery-category/${categoryId}/edit`}
                  aria-label={`${categoryName ?? '연결'} 배송 카테고리 수정`}
                  className="hover:text-indigo-700 hover:underline"
                >
                  {categoryName ?? '카테고리 확인'} ↗
                </Link>
              ) : (
                '카테고리 미지정'
              )}
              {item.subCategory && <span className="ml-1">· {item.subCategory}</span>}
            </div>
          )}
        </td>
        <td className={cell}>
          <p className="text-sm font-semibold tabular-nums text-gray-900">
            <span className="mr-2 text-xs font-normal text-gray-500">판매</span>
            {(item.priceRetail ?? 0).toLocaleString()}원
          </p>
          <p className="mt-1 text-xs tabular-nums text-gray-500">
            도매 {(item.priceWhole ?? 0).toLocaleString()}원 · 정가{' '}
            {(item.priceRegular ?? 0).toLocaleString()}원
          </p>
          {margin !== null && (
            <p className="mt-1 text-xs tabular-nums text-gray-600">
              마진 {(item.priceRetail - item.priceWhole).toLocaleString()}원 (
              {Math.round(margin)}%)
            </p>
          )}
          {topMargin && (
            <p
              className="mt-1 text-xs text-indigo-700"
              title="같은 배송 카테고리와 하위분류 중 마진율이 가장 높습니다."
            >
              하위분류 최고마진
            </p>
          )}
        </td>
        <td className={cell}>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded border px-2 py-0.5 text-xs ${
                item.isEnabled
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-gray-200 bg-gray-50 text-gray-500'
              }`}
            >
              {item.isEnabled ? '활성' : '비활성'}
            </span>
            <span className="text-xs text-gray-500">
              {item.type === GiftItemInfoType.Delivery ? '배송형' : '쿠폰형'} ·{' '}
              {item.isTaxable !== false ? '과세' : '비과세'}
            </span>
          </div>
        </td>
        <td className={cell}>
          <div className="flex flex-wrap items-center gap-2 xl:justify-end">
            <Link
              to={editUrl}
              aria-label={`${item.name} 수정`}
              className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              수정
            </Link>
            {(Boolean(item.tags?.length) ||
              (item.isSelectable && Boolean(item.choiceUnits?.length))) && (
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={`item-info-detail-${item._id}`}
                onClick={() => setExpanded(!expanded)}
                className="rounded px-2 py-2 text-xs text-gray-500 hover:bg-gray-50"
              >
                {expanded ? '상세 접기' : '상세 보기'}
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr id={`item-info-detail-${item._id}`} className="block bg-gray-50 xl:table-row">
          <td colSpan={5} className="block space-y-4 px-5 py-4 xl:table-cell">
            {Boolean(item.tags?.length) && (
              <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                <span className="font-medium">검색 태그</span>
                {item.tags.map((tag, i) => (
                  <span key={`${tag}-${i}`}>#{tag}</span>
                ))}
              </div>
            )}
            {item.isSelectable && Boolean(item.choiceUnits?.length) && (
              <div>
                <h3 className="mb-2 text-xs font-semibold text-gray-700">
                  연결 선택유닛 · {item.choiceUnits.length}개
                </h3>
                <div className="flex flex-wrap gap-2">
                  {item.choiceUnits.map((unit) => {
                    const id = typeof unit === 'object' ? unit._id : unit;
                    return (
                      <Link
                        key={id}
                        to={`/gift-seller/choice-unit/${id}/edit`}
                        className="flex max-w-full items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:border-gray-400"
                      >
                        {typeof unit === 'object' && unit.image && (
                          <img
                            src={fileBucketUrl(
                              typeof unit.image === 'object'
                                ? unit.image.url
                                : unit.image,
                              'raw',
                            )}
                            alt=""
                            className="h-8 w-8 rounded object-cover"
                          />
                        )}
                        <span className="min-w-0 break-words">
                          {typeof unit === 'object' ? unit.name : '선택유닛 확인'} ↗
                          {typeof unit === 'object' && (
                            <span className="mt-1 block text-xs text-gray-500">
                              정가 {(unit.priceRegular ?? 0).toLocaleString()}원 · 도매{' '}
                              {(unit.priceWhole ?? 0).toLocaleString()}원
                            </span>
                          )}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};
